import React, { useEffect, useState } from 'react';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { businessInfo } from '../../mockData';
import { API_URL } from '../../lib/shopApi';
import DoorPhotos from './DoorPhotos';

// Posts to the existing /api/contact endpoint (server.py) with a shop-specific
// pageSource so trade enquiries are recognisable in Hugh's inbox.
export default function TradeQuoteForm({ pageSource = 'shop-trade', initialMessage = '', projectType = 'Trade / property management', submitLabel = 'Request a quote', thanks = 'Thanks — we will come back to you with a quote.', photos: withPhotos = true }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: initialMessage, honeypot: '' });
  const [photos, setPhotos] = useState([]);
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');
  // The A200 estimator rebuilds initialMessage as options and activation points change;
  // follow it until the customer has typed in the box themselves.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) setForm((f) => ({ ...f, message: initialMessage }));
  }, [initialMessage, touched]);
  const set = (e) => {
    if (e.target.name === 'message') setTouched(true);
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setState('sending');
    setError('');
    if (!API_URL) {
      setState('error');
      setError(`Online enquiries are briefly unavailable — email ${businessInfo.email} or call ${businessInfo.phone}.`);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, projectType, pageSource, photos: photos.map((p) => ({ filename: p.name, contentType: p.type, data: p.data })) }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Could not send');
      setState('sent');
    } catch (err) {
      setState('error');
      setError(typeof err.message === 'string' ? err.message : 'Could not send — please call us.');
    }
  };

  if (state === 'sent') {
    return (
      <p className="flex items-start gap-2 text-green-700" role="status">
        <CheckCircle className="mt-0.5" size={18} /> {thanks}
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="trade-quote-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="tq-name">Name</Label>
          <Input id="tq-name" name="name" required minLength={2} value={form.name} onChange={set} />
        </div>
        <div>
          <Label htmlFor="tq-company">Company / portfolio</Label>
          <Input id="tq-company" name="company" value={form.company} onChange={set} />
        </div>
        <div>
          <Label htmlFor="tq-email">Email</Label>
          <Input id="tq-email" name="email" type="email" required value={form.email} onChange={set} />
        </div>
        <div>
          <Label htmlFor="tq-phone">Phone</Label>
          <Input id="tq-phone" name="phone" value={form.phone} onChange={set} />
        </div>
      </div>
      <div>
        <Label htmlFor="tq-message">What are you managing, and what do you need?</Label>
        <Textarea id="tq-message" name="message" required minLength={10} rows={5} value={form.message} onChange={set} placeholder="e.g. 12 rentals on the Shore — keypad locks on each front door, and key safes for the cleaners" />
      </div>
      {withPhotos && (
        <div>
          <Label>Photos of the door (optional, up to 6)</Label>
          <p className="text-xs text-gray-600 mb-2">The door edge with a tape across the lock, the inside, and the frame where the lock latches. That is enough for a firm quote on most doors.</p>
          <DoorPhotos photos={photos} onChange={setPhotos} showGuide={false} testId="trade-quote-photos" />
        </div>
      )}
      <input type="text" name="honeypot" value={form.honeypot} onChange={set} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      {error && <p className="text-sm text-red-700">{error}</p>}
      <Button type="submit" disabled={state === 'sending'} className="bg-red-600 hover:bg-red-700 text-white font-bold">
        {state === 'sending' ? <Loader2 className="mr-2 animate-spin" size={18} /> : <Send className="mr-2" size={18} />}
        {submitLabel}
      </Button>
    </form>
  );
}
